import React, { useState } from 'react';
import {
    Card,
    Button,
    Alert,
    Typography,
    Space,
    message,
    Table,
    Tag,
    Tooltip,
    Descriptions,
    Select,
    Modal,
    Statistic,
    Row,
    Col,
    Badge,
} from 'antd';
import {
    DatabaseOutlined,
    DownloadOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    SyncOutlined,
    PlayCircleOutlined,
    ScheduleOutlined,
    UserOutlined,
    ExclamationCircleOutlined,
    CloudServerOutlined,
    HddOutlined,
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAuth } from '../../../hooks/useAuth';
import {
    useCreateDatabaseBackupMutation,
    useGetDatabaseBackupsQuery,
    useGetDatabaseBackupStatusQuery,
    useGetDatabaseBackupDownloadUrlMutation,
    useDeleteDatabaseBackupMutation,
} from '../../../services/nestjs-base-api';

const { Title, Text } = Typography;

const STATUS_CONFIG = {
    COMPLETED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed' },
    FAILED: { color: 'error', icon: <CloseCircleOutlined />, label: 'Failed' },
    RUNNING: { color: 'processing', icon: <SyncOutlined spin />, label: 'Running' },
} as const;

const TRIGGER_CONFIG = {
    SCHEDULED: { color: 'blue', icon: <ScheduleOutlined style={{ marginRight: 4 }} />, label: 'Scheduled' },
    MANUAL: { color: 'purple', icon: <UserOutlined style={{ marginRight: 4 }} />, label: 'Manual' },
} as const;

interface DatabaseBackupTabProps {
    onNavigateToSettings?: () => void;
}

export const DatabaseBackupTab: React.FC<DatabaseBackupTabProps> = ({ onNavigateToSettings }) => {
    const { user } = useAuth();
    const intl = useIntl();
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [triggerFilter, setTriggerFilter] = useState<string | undefined>(undefined);

    const [createBackup, { isLoading: isCreating }] = useCreateDatabaseBackupMutation();
    const [getDownloadUrl] = useGetDatabaseBackupDownloadUrlMutation();
    const [deleteBackup] = useDeleteDatabaseBackupMutation();

    const { data: statusData, isLoading: statusLoading } = useGetDatabaseBackupStatusQuery(
        { jwtToken: user?.jwtToken || '' },
        { pollingInterval: 30000 }
    );

    const { data: backupsData, isLoading: backupsLoading } = useGetDatabaseBackupsQuery(
        {
            jwtToken: user?.jwtToken || '',
            page,
            limit: 15,
            status: statusFilter,
            triggerType: triggerFilter,
        },
        { pollingInterval: 30000 }
    );

    const handleCreateBackup = async () => {
        try {
            await createBackup({ jwtToken: user?.jwtToken || '' }).unwrap();
            message.success(intl.formatMessage({ id: 'admin.backup.created', defaultMessage: 'Database backup created successfully' }));
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'admin.backup.failed', defaultMessage: 'Failed to create database backup' }));
        }
    };

    const handleDownload = async (backupId: number) => {
        try {
            const result = await getDownloadUrl({
                jwtToken: user?.jwtToken || '',
                backupId,
            }).unwrap();
            window.open(result.downloadUrl, '_blank');
            message.success(intl.formatMessage({ id: 'admin.backup.downloadStarted', defaultMessage: 'Download started' }));
        } catch (error: any) {
            message.error(error?.data?.message || intl.formatMessage({ id: 'admin.backup.downloadFailed', defaultMessage: 'Failed to get download URL' }));
        }
    };

    const handleDelete = (backupId: number, filename: string) => {
        Modal.confirm({
            title: intl.formatMessage({ id: 'admin.backup.deleteConfirmTitle', defaultMessage: 'Delete Backup' }),
            icon: <ExclamationCircleOutlined />,
            content: intl.formatMessage(
                { id: 'admin.backup.deleteConfirmContent', defaultMessage: 'Are you sure you want to delete backup "{filename}"? This will remove the file from S3 and cannot be undone.' },
                { filename }
            ),
            okText: intl.formatMessage({ id: 'common.delete', defaultMessage: 'Delete' }),
            okType: 'danger',
            cancelText: intl.formatMessage({ id: 'common.cancel', defaultMessage: 'Cancel' }),
            onOk: async () => {
                try {
                    await deleteBackup({
                        jwtToken: user?.jwtToken || '',
                        backupId,
                    }).unwrap();
                    message.success(intl.formatMessage({ id: 'admin.backup.deleted', defaultMessage: 'Backup deleted successfully' }));
                } catch (error: any) {
                    message.error(error?.data?.message || intl.formatMessage({ id: 'admin.backup.deleteFailed', defaultMessage: 'Failed to delete backup' }));
                }
            },
        });
    };

    const getHealthStatus = (): { status: 'success' | 'warning' | 'error'; text: string } => {
        if (!statusData) return { status: 'warning', text: 'Loading...' };
        if (!statusData.backupEnabled) return { status: 'warning', text: 'Automated backups disabled' };
        if (!statusData.lastBackup) return { status: 'warning', text: 'No backups yet' };

        if (statusData.lastBackup.status === 'FAILED') {
            return { status: 'error', text: 'Last backup failed' };
        }

        // Check if last backup is stale (> 1.5x the schedule interval)
        const lastBackupDate = new Date(statusData.lastBackup.date);
        const hoursSince = (Date.now() - lastBackupDate.getTime()) / (1000 * 60 * 60);

        if (hoursSince > 24) {
            return { status: 'error', text: `No backup in ${Math.floor(hoursSince)} hours` };
        }

        // Parse simple "every N hours" from cron
        const cronMatch = statusData.schedule.match(/\*\/(\d+)/);
        const intervalHours = cronMatch ? parseInt(cronMatch[1], 10) : 4;
        if (hoursSince > intervalHours * 1.5) {
            return { status: 'warning', text: `Last backup ${Math.floor(hoursSince)} hours ago` };
        }

        return { status: 'success', text: 'Backups running normally' };
    };

    const health = getHealthStatus();

    const formatDuration = (seconds: number | null): string => {
        if (seconds === null) return '-';
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const formatSize = (mb: number | string | null): string => {
        if (mb === null || mb === undefined) return '-';
        const val = typeof mb === 'string' ? parseFloat(mb) : mb;
        if (isNaN(val)) return '-';
        if (val < 1) return `${(val * 1024).toFixed(0)} KB`;
        if (val >= 1024) return `${(val / 1024).toFixed(2)} GB`;
        return `${val.toFixed(2)} MB`;
    };

    const formatRelativeTime = (dateStr: string | null): string => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        const absDiffMs = Math.abs(diffMs);
        const hours = Math.floor(absDiffMs / (1000 * 60 * 60));
        const mins = Math.floor((absDiffMs % (1000 * 60 * 60)) / (1000 * 60));

        if (diffMs > 0) {
            // Future
            if (hours > 0) return `in ${hours}h ${mins}m`;
            return `in ${mins}m`;
        } else {
            // Past
            if (hours > 24) return `${Math.floor(hours / 24)}d ago`;
            if (hours > 0) return `${hours}h ${mins}m ago`;
            return `${mins}m ago`;
        }
    };

    const columns = [
        {
            title: <FormattedMessage id="admin.backup.col.date" defaultMessage="Date" />,
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (val: string) => (
                <Tooltip title={new Date(val).toLocaleString()}>
                    <Text>{new Date(val).toLocaleString()}</Text>
                </Tooltip>
            ),
        },
        {
            title: <FormattedMessage id="admin.backup.col.status" defaultMessage="Status" />,
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status: keyof typeof STATUS_CONFIG) => {
                const config = STATUS_CONFIG[status];
                return (
                    <Tag icon={config.icon} color={config.color}>
                        {config.label}
                    </Tag>
                );
            },
        },
        {
            title: <FormattedMessage id="admin.backup.col.trigger" defaultMessage="Trigger" />,
            dataIndex: 'triggerType',
            key: 'triggerType',
            width: 120,
            render: (trigger: keyof typeof TRIGGER_CONFIG) => {
                const config = TRIGGER_CONFIG[trigger];
                return (
                    <Tag color={config.color}>
                        {config.icon}
                        {config.label}
                    </Tag>
                );
            },
        },
        {
            title: <FormattedMessage id="admin.backup.col.size" defaultMessage="Size" />,
            dataIndex: 'fileSizeMb',
            key: 'fileSizeMb',
            width: 100,
            render: (val: number | null) => <Text>{formatSize(val)}</Text>,
        },
        {
            title: <FormattedMessage id="admin.backup.col.duration" defaultMessage="Duration" />,
            dataIndex: 'durationSeconds',
            key: 'durationSeconds',
            width: 100,
            render: (val: number | null) => <Text>{formatDuration(val)}</Text>,
        },
        {
            title: <FormattedMessage id="admin.backup.col.expires" defaultMessage="Expires" />,
            dataIndex: 'expiresAt',
            key: 'expiresAt',
            width: 140,
            render: (val: string, record: any) => {
                if (record.isDeleted) return <Tag color="default">Deleted</Tag>;
                const isExpired = new Date(val) < new Date();
                return (
                    <Tooltip title={new Date(val).toLocaleString()}>
                        <Text type={isExpired ? 'danger' : undefined}>
                            {isExpired ? 'Expired' : formatRelativeTime(val)}
                        </Text>
                    </Tooltip>
                );
            },
        },
        {
            title: <FormattedMessage id="admin.backup.col.actions" defaultMessage="Actions" />,
            key: 'actions',
            width: 120,
            render: (_: any, record: any) => (
                <Space size="small">
                    {record.status === 'COMPLETED' && !record.isDeleted && (
                        <Tooltip title={intl.formatMessage({ id: 'admin.backup.download', defaultMessage: 'Download' })}>
                            <Button
                                type="link"
                                size="small"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDownload(record.id)}
                            />
                        </Tooltip>
                    )}
                    {!record.isDeleted && record.status !== 'RUNNING' && (
                        <Tooltip title={intl.formatMessage({ id: 'admin.backup.delete', defaultMessage: 'Delete' })}>
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record.id, record.filename)}
                            />
                        </Tooltip>
                    )}
                    {record.status === 'FAILED' && record.errorMessage && (
                        <Tooltip title={record.errorMessage}>
                            <Button
                                type="link"
                                size="small"
                                icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                            />
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* Health Status Banner */}
            <Alert
                message={
                    <Space>
                        <Badge status={health.status} />
                        <Text strong>
                            <FormattedMessage id="admin.backup.health" defaultMessage="Backup Health" />
                        </Text>
                        <Text>{health.text}</Text>
                    </Space>
                }
                type={health.status === 'success' ? 'success' : health.status === 'warning' ? 'warning' : 'error'}
                showIcon
                style={{ marginBottom: 24 }}
            />

            {/* Status Summary Cards */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={statusLoading} size="small">
                        <Statistic
                            title={<FormattedMessage id="admin.backup.totalBackups" defaultMessage="Total Backups" />}
                            value={statusData?.totalBackups ?? 0}
                            prefix={<DatabaseOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={statusLoading} size="small">
                        <Statistic
                            title={<FormattedMessage id="admin.backup.totalSize" defaultMessage="Total Storage" />}
                            value={formatSize(statusData?.totalSizeMb ?? 0)}
                            prefix={<HddOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={statusLoading} size="small">
                        <Statistic
                            title={<FormattedMessage id="admin.backup.lastBackup" defaultMessage="Last Backup" />}
                            value={statusData?.lastBackup ? formatRelativeTime(statusData.lastBackup.date) : 'None'}
                            prefix={<ClockCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card loading={statusLoading} size="small">
                        <Statistic
                            title={<FormattedMessage id="admin.backup.nextBackup" defaultMessage="Next Scheduled" />}
                            value={statusData?.nextScheduledAt ? formatRelativeTime(statusData.nextScheduledAt) : 'Disabled'}
                            prefix={<ScheduleOutlined />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Configuration Summary */}
            <Card
                size="small"
                style={{ marginBottom: 24 }}
                title={
                    <span>
                        <CloudServerOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="admin.backup.configTitle" defaultMessage="Configuration" />
                    </span>
                }
                extra={
                    onNavigateToSettings && (
                        <Button type="link" size="small" onClick={onNavigateToSettings}>
                            <FormattedMessage
                                id="admin.backup.configEdit"
                                defaultMessage="Edit Configuration"
                            />
                        </Button>
                    )
                }
            >
                {statusData && (
                    <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                        <Descriptions.Item
                            label={<FormattedMessage id="admin.backup.enabled" defaultMessage="Enabled" />}
                        >
                            <Tag color={statusData.backupEnabled ? 'success' : 'default'}>
                                {statusData.backupEnabled ? 'Yes' : 'No'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={<FormattedMessage id="admin.backup.schedule" defaultMessage="Schedule" />}
                        >
                            <Text code>{statusData.schedule}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={<FormattedMessage id="admin.backup.retention" defaultMessage="Retention" />}
                        >
                            {statusData.retentionDays} days
                        </Descriptions.Item>
                        <Descriptions.Item
                            label={<FormattedMessage id="admin.backup.minRetained" defaultMessage="Min Retained" />}
                        >
                            {statusData.minRetained}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Card>

            {/* Backup History */}
            <Card
                title={
                    <span>
                        <DatabaseOutlined style={{ marginRight: 8 }} />
                        <FormattedMessage id="admin.backup.history" defaultMessage="Backup History" />
                    </span>
                }
                extra={
                    <Space>
                        <Select
                            placeholder={intl.formatMessage({ id: 'admin.backup.filterStatus', defaultMessage: 'Status' })}
                            allowClear
                            style={{ width: 130 }}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            options={[
                                { value: 'COMPLETED', label: 'Completed' },
                                { value: 'FAILED', label: 'Failed' },
                                { value: 'RUNNING', label: 'Running' },
                            ]}
                        />
                        <Select
                            placeholder={intl.formatMessage({ id: 'admin.backup.filterTrigger', defaultMessage: 'Trigger' })}
                            allowClear
                            style={{ width: 130 }}
                            value={triggerFilter}
                            onChange={setTriggerFilter}
                            options={[
                                { value: 'SCHEDULED', label: 'Scheduled' },
                                { value: 'MANUAL', label: 'Manual' },
                            ]}
                        />
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined style={{ marginRight: 4 }} />}
                            onClick={handleCreateBackup}
                            loading={isCreating}
                        >
                            <FormattedMessage id="admin.backup.createNow" defaultMessage="Backup Now" />
                        </Button>
                    </Space>
                }
            >
                <Table
                    dataSource={backupsData?.data || []}
                    columns={columns}
                    rowKey="id"
                    loading={backupsLoading}
                    size="small"
                    pagination={{
                        current: page,
                        total: backupsData?.total || 0,
                        pageSize: 15,
                        showSizeChanger: false,
                        showTotal: (total) => intl.formatMessage(
                            { id: 'admin.backup.totalItems', defaultMessage: '{total} backups' },
                            { total }
                        ),
                        onChange: setPage,
                    }}
                />
            </Card>

            {/* Restore Instructions */}
            <Card
                title={
                    <FormattedMessage id="admin.backup.restoreTitle" defaultMessage="How to Restore from Backup" />
                }
                style={{ marginTop: 24 }}
                type="inner"
            >
                <Text>
                    <FormattedMessage
                        id="admin.backup.restoreInstructions"
                        defaultMessage="To restore the database from a backup file, use the following command:"
                    />
                </Text>
                <pre style={{
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    overflow: 'auto',
                    marginTop: 8,
                }}>
                    {`mysql -h <HOST> -u <USERNAME> -p <DATABASE_NAME> < backup-YYYY-MM-DD-HH-mm-ss.sql`}
                </pre>
                <Alert
                    message={<FormattedMessage id="admin.backup.restoreWarning" defaultMessage="Restoration Warning" />}
                    description={
                        <FormattedMessage
                            id="admin.backup.restoreWarningDesc"
                            defaultMessage="Restoring a backup will overwrite all existing data in the database. Always verify you are restoring to the correct database and consider creating a backup of the current state first."
                        />
                    }
                    type="error"
                    showIcon
                    style={{ marginTop: 12 }}
                />
            </Card>
        </div>
    );
};
